import CheckCircle from '@mui/icons-material/CheckCircle';
import ChevronRight from '@mui/icons-material/ChevronRight';
import Search from '@mui/icons-material/Search';
import Box from '@mui/material/Box';
import InputBase from '@mui/material/InputBase';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { type ReactNode, useDeferredValue, useMemo, useState } from 'react';
import {
  CARD_SX,
  DIVIDER,
  NetworkIcon,
  OutlineButton,
  ScoreValue,
  TEXT_SOFT,
  TEXT_STRONG,
} from './FlightCheckParts.js';
import type {
  AnalysisMock,
  BatchSummary,
  LatestBatchStrip as LatestBatchStripData,
  NetworkRow,
} from './flightCheckMocks.js';
import { FUNNEL_STAGES, type FunnelStage } from './flightCheckMocks.js';
import { formatRanAt, formatScore, MAX_SCORE, scoreBand, scoreText } from './scoreScale.js';

const COLUMNS = [
  'Platform / score',
  'Best practice',
  'Performance',
  'Brand guidelines',
  'Weakest category',
  'Lowest creative',
  'Type',
] as const;

const GRID_SX = {
  display: 'grid',
  gridTemplateColumns:
    'minmax(170px, 250px) repeat(2, minmax(80px, 120px)) minmax(90px, 130px) repeat(2, minmax(100px, 170px)) minmax(60px, 90px) 1fr',
  columnGap: '8px',
  alignItems: 'center',
  px: '16px',
};

const STAT_LABEL_SX = {
  fontSize: 11,
  fontWeight: 600,
  letterSpacing: '0.66px',
  textTransform: 'uppercase',
  color: TEXT_SOFT,
  lineHeight: 'normal',
  whiteSpace: 'nowrap',
};

function ScoreBar({ score, width }: { score: number; width: number }): ReactNode {
  const theme = useTheme();
  return (
    <Box
      role="progressbar"
      aria-valuenow={score}
      aria-valuemin={0}
      aria-valuemax={MAX_SCORE}
      sx={{
        width,
        height: 6,
        borderRadius: '3px',
        bgcolor: DIVIDER,
        overflow: 'hidden',
        flexShrink: 0,
      }}
    >
      <Box
        sx={{
          width: `${(score / MAX_SCORE) * 100}%`,
          height: '100%',
          borderRadius: '3px',
          bgcolor: theme.palette.score[scoreBand(score)].main,
        }}
      />
    </Box>
  );
}

function StatDivider(): ReactNode {
  return <Box sx={{ width: '1px', height: 48, bgcolor: DIVIDER, flexShrink: 0 }} />;
}

function Stat({ label, children }: { label: string; children: ReactNode }): ReactNode {
  return (
    <Stack sx={{ gap: '4px', flexShrink: 0 }}>
      <Typography sx={STAT_LABEL_SX}>{label}</Typography>
      <Stack direction="row" sx={{ alignItems: 'baseline', gap: '6px' }}>
        {children}
      </Stack>
    </Stack>
  );
}

function ViewBatchLink(): ReactNode {
  return (
    <OutlineButton>
      View batch
      <ChevronRight sx={{ fontSize: 16, ml: '2px', mr: '-4px' }} />
    </OutlineButton>
  );
}

function LatestBatchStrip({ latest }: { latest: LatestBatchStripData }): ReactNode {
  return (
    <Stack
      data-testid="latest-batch"
      sx={{ ...CARD_SX, borderRadius: '16px', px: '25px', pt: '19px', pb: '21px', gap: '18px' }}
    >
      <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
        <Stack direction="row" sx={{ alignItems: 'center', gap: '10px', minWidth: 0 }}>
          <CheckCircle sx={{ fontSize: 24, color: 'score.high.main' }} />
          <Typography sx={{ fontSize: 14, fontWeight: 600, color: TEXT_STRONG, flexShrink: 0 }}>
            Latest batch
          </Typography>
          <Typography noWrap sx={{ fontSize: 13, color: TEXT_SOFT }}>
            <Box component="span" sx={{ fontWeight: 500, color: TEXT_STRONG }}>
              {latest.name}
            </Box>
            {` · ${formatRanAt(latest.ranAt)} · ${latest.creativeCount} creatives analyzed`}
          </Typography>
        </Stack>
        <ViewBatchLink />
      </Stack>
      <Stack
        direction="row"
        sx={{ alignItems: 'center', gap: '36px', flexWrap: 'wrap', rowGap: '16px' }}
      >
        <Stack sx={{ gap: '6px', width: 140 }}>
          <Stack direction="row" sx={{ alignItems: 'baseline', gap: '4px' }}>
            <ScoreValue score={latest.overall} size={34} />
            <Typography sx={{ fontSize: 13, color: TEXT_SOFT }}>/ 10</Typography>
          </Stack>
          <ScoreBar score={latest.overall} width={140} />
        </Stack>
        <StatDivider />
        <Stat label="Best practice">
          <ScoreValue score={latest.bestPractice} size={22} />
        </Stat>
        <Stat label="Performance">
          <ScoreValue score={latest.performance} size={22} />
        </Stat>
        <Stat label="Brand guidelines">
          <ScoreValue score={latest.brandGuidelines} size={22} />
        </Stat>
        <StatDivider />
        <Stat label="Weakest category">
          <Typography
            sx={{ fontSize: 22, fontWeight: 500, color: 'score.low.text', lineHeight: 'normal' }}
          >
            {`${latest.weakest.percent}%`}
          </Typography>
          <Typography sx={{ fontSize: 13, color: TEXT_STRONG }}>{latest.weakest.name}</Typography>
        </Stat>
        <Stack sx={{ gap: '4px', flexShrink: 0 }}>
          <Typography sx={STAT_LABEL_SX}>By platform</Typography>
          <Stack direction="row" sx={{ gap: '14px' }}>
            {latest.networks.map(({ network, score }) => (
              <Stack key={network} direction="row" sx={{ alignItems: 'center', gap: '5px' }}>
                <NetworkIcon network={network} size={20} />
                <ScoreValue score={score} size={15} weight={600} />
              </Stack>
            ))}
          </Stack>
        </Stack>
        <Stack sx={{ gap: '4px', flexShrink: 0 }}>
          <Typography sx={STAT_LABEL_SX}>Needs attention</Typography>
          <Box
            component="span"
            sx={{
              alignSelf: 'flex-start',
              px: '8px',
              py: '3px',
              borderRadius: '10px',
              bgcolor: 'score.low.bg',
              color: 'score.low.text',
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: '0.4px',
              lineHeight: 'normal',
            }}
          >
            {`${latest.needsAttention} CREATIVES`}
          </Box>
        </Stack>
      </Stack>
    </Stack>
  );
}

function PlatformRow({ row }: { row: NetworkRow }): ReactNode {
  const theme = useTheme();
  const metric = (value: number): ReactNode => (
    <Typography sx={{ fontSize: 14, fontWeight: 500, color: scoreText(theme, value) }}>
      {formatScore(value)}
    </Typography>
  );
  return (
    <Box
      data-testid="platform-row"
      sx={{ ...GRID_SX, minHeight: 55, py: '8px', borderTop: `1px solid ${DIVIDER}` }}
    >
      <Stack direction="row" sx={{ alignItems: 'center', gap: '8px' }}>
        <NetworkIcon network={row.network} size={22} />
        <ScoreValue score={row.score} size={15} weight={600} />
        <ScoreBar score={row.score} width={90} />
      </Stack>
      {metric(row.bestPractice)}
      {metric(row.performance)}
      {metric(row.brandGuidelines)}
      <Typography sx={{ fontSize: 13, color: TEXT_STRONG }}>
        {row.weakest.name}
        <Box component="span" sx={{ ml: '8px', color: TEXT_SOFT }}>
          {`${row.weakest.percent}%`}
        </Box>
      </Typography>
      <Box sx={{ position: 'relative', width: 48, height: 40 }}>
        <Box
          component="img"
          src={row.lowestCreative.thumbnailUrl}
          alt={`Lowest ${row.network} creative`}
          sx={{ width: 40, height: 38, objectFit: 'cover', borderRadius: '6px', display: 'block' }}
        />
        <Box
          component="span"
          sx={{
            position: 'absolute',
            right: 0,
            bottom: 0,
            px: '5px',
            py: '2px',
            borderRadius: '6px',
            bgcolor: 'background.paper',
            border: `1px solid ${DIVIDER}`,
            fontSize: 11,
            fontWeight: 600,
            lineHeight: 'normal',
            color: scoreText(theme, row.lowestCreative.score),
          }}
        >
          {formatScore(row.lowestCreative.score)}
        </Box>
      </Box>
      <Typography sx={{ fontSize: 13, color: TEXT_STRONG }}>{row.type}</Typography>
    </Box>
  );
}

function BatchGroup({ batch }: { batch: BatchSummary }): ReactNode {
  return (
    <Box data-testid="batch-group">
      <Stack
        direction="row"
        sx={{
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          minHeight: 52,
          px: '16px',
          mt: '4px',
          bgcolor: 'background.page',
          borderRadius: '8px',
        }}
      >
        <Stack direction="row" sx={{ alignItems: 'baseline', gap: '10px', minWidth: 0 }}>
          <Typography
            noWrap
            title={batch.name}
            sx={{ fontSize: 14, fontWeight: 600, color: TEXT_STRONG }}
          >
            {batch.name}
          </Typography>
          <Typography noWrap sx={{ fontSize: 12, color: TEXT_SOFT }}>
            {formatRanAt(batch.ranAt)}
          </Typography>
        </Stack>
        <Stack direction="row" sx={{ alignItems: 'center', gap: '16px', flexShrink: 0 }}>
          <Typography sx={{ fontSize: 12, color: TEXT_SOFT }}>
            {`${batch.creativeCount} creatives analyzed`}
          </Typography>
          <ViewBatchLink />
        </Stack>
      </Stack>
      {batch.rows.map((row) => (
        <PlatformRow key={row.network} row={row} />
      ))}
    </Box>
  );
}

function AllBatches({ batches }: { batches: BatchSummary[] }): ReactNode {
  const [search, setSearch] = useState('');
  const [stage, setStage] = useState<FunnelStage | ''>('');
  const query = useDeferredValue(search.trim().toLowerCase());
  const shown = useMemo(
    () =>
      batches.filter(
        (batch) =>
          batch.name.toLowerCase().includes(query) && (stage === '' || batch.funnelStage === stage),
      ),
    [batches, query, stage],
  );

  return (
    <Stack
      sx={{ ...CARD_SX, borderRadius: '16px', px: '25px', pt: '21px', pb: '21px', gap: '16px' }}
    >
      <Typography sx={{ fontSize: 16, fontWeight: 500, color: TEXT_STRONG }}>
        All batches
      </Typography>
      <Stack direction="row" sx={{ gap: '12px', flexWrap: 'wrap' }}>
        <Stack
          direction="row"
          sx={{
            alignItems: 'center',
            gap: '8px',
            width: 320,
            maxWidth: '100%',
            height: 34,
            px: '13px',
            border: '1px solid',
            borderColor: 'border.default',
            borderRadius: '9px',
          }}
        >
          <Search sx={{ fontSize: 16, color: 'text.muted' }} />
          <InputBase
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search by batch name"
            inputProps={{ 'aria-label': 'Search by batch name' }}
            sx={{ flex: 1, fontSize: 12, color: TEXT_STRONG }}
          />
        </Stack>
        <Select
          value={stage}
          onChange={(event) => setStage(event.target.value)}
          displayEmpty
          inputProps={{ 'aria-label': 'Funnel stage' }}
          renderValue={(value) => (
            <Stack direction="row" sx={{ alignItems: 'center', gap: '8px' }}>
              <Typography
                component="span"
                sx={{ fontSize: 9, fontWeight: 600, letterSpacing: '0.9px', color: 'text.muted' }}
              >
                FUNNEL STAGE
              </Typography>
              <Typography component="span" sx={{ fontSize: 12, color: TEXT_STRONG }}>
                {value || 'All stages'}
              </Typography>
            </Stack>
          )}
          sx={{
            minWidth: 200,
            bgcolor: 'background.paper',
            borderRadius: '9px',
            '& .MuiSelect-select': { py: '9px', pl: '11px' },
            '& .MuiOutlinedInput-notchedOutline': { borderColor: 'border.default' },
          }}
        >
          <MenuItem value="">All stages</MenuItem>
          {FUNNEL_STAGES.map((option) => (
            <MenuItem key={option} value={option}>
              {option}
            </MenuItem>
          ))}
        </Select>
      </Stack>
      <Box sx={{ overflowX: 'auto' }}>
        <Box sx={{ minWidth: 1080 }}>
          <Box sx={{ ...GRID_SX, height: 34 }}>
            {COLUMNS.map((column) => (
              <Typography key={column} sx={STAT_LABEL_SX}>
                {column}
              </Typography>
            ))}
          </Box>
          {shown.length === 0 ? (
            <Typography sx={{ px: '16px', py: '24px', fontSize: 13, color: TEXT_SOFT }}>
              {stage === ''
                ? `No batches named '${search.trim()}'`
                : 'No batches match these filters.'}
            </Typography>
          ) : (
            shown.map((batch) => <BatchGroup key={batch.id} batch={batch} />)
          )}
        </Box>
      </Box>
    </Stack>
  );
}

export function FlightCheckAnalysis({ data }: { data: AnalysisMock }): ReactNode {
  return (
    <Stack sx={{ gap: '32px', minWidth: 0 }}>
      <LatestBatchStrip latest={data.latest} />
      <AllBatches batches={data.batches} />
    </Stack>
  );
}
